import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import useScrollAnimation from '../../hooks/useScrollAnimation';
import useAutoHeightAdjustment from '../../hooks/useAutoHeightAdjustment';
import { 
  FaTools, 
  FaPhone,
  FaCheckCircle,
  FaUsers,
  FaClipboardCheck,
  FaArrowLeft,
  FaHardHat,
  FaImages,
  FaAward,
  FaHome,
  FaBuilding,
  FaPaintRoller,
  FaWrench,
  FaBars,
  FaTimes,
  FaShieldAlt,
  FaWindowMaximize
} from 'react-icons/fa';
import ContactModal from '../ui/ContactModal';
import ReusableDropdown from '../ui/ReusableDropdown.jsx';
import BeforeAfterVideoSlider from '../ui/BeforeAfterVideoSlider';
import { Footer } from '../index';
import './RoofingPage.css';
import '../layout/PageMobileNav.css';

const RoofingPage = () => {
  const navigate = useNavigate();
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const titleRef = useScrollAnimation();
  const servicesRef = useScrollAnimation();
  const installationRef = useScrollAnimation();
  const whyTrustUsRef = useScrollAnimation();
  const ctaRef = useScrollAnimation();
  const videoRef = useScrollAnimation();

  // Use the auto height adjustment hook for service cards
  const { sameHeightRef: servicesHeightRef, refresh: refreshServicesHeights } = useAutoHeightAdjustment({
    containerSelector: '.roofing-services-grid.same-height',
    elementSelector: '[data-same-height]',
    debounceDelay: 200,
    observeChildList: true,
    observeAttributes: true,
    observeCharacterData: true,
    subtree: true
  });

  const handleContactClick = (e) => {
    e.preventDefault();
    window.location.href = 'tel:+13136316020';
  };

  // Navigation services for dropdown
  const navigationServices = [
    { id: 'roofing', text: 'Roofing', to: '/roofing', icon: FaHome },
    { id: 'windows', text: 'Windows', to: '/windows', icon: FaWindowMaximize },
    { id: 'siding', text: 'Siding', to: '/siding', icon: FaPaintRoller },
    { id: 'gutters', text: 'Gutters', to: '/gutters', icon: FaTools },
    { id: 'repairs', text: 'Repairs', to: '/repairs', icon: FaWrench },
    { id: 'all-services', text: 'All Services', to: '/all-services', icon: FaHardHat }
  ];

  // Mobile menu handlers
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(prev => !prev);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Debug: Check if image loads
  useEffect(() => {
    const img1 = new Image();
    img1.onload = () => console.log('Roofing hero image (roofing-8.webp) loaded successfully');
    img1.onerror = () => console.log('Failed to load roofing hero image (roofing-8.webp)');
    img1.src = '/images/services/roofing/roofing-8.webp';

    const img2 = new Image();
    img2.onload = () => console.log('Services hero image (roofing-1.webp) loaded successfully');
    img2.onerror = () => console.log('Failed to load services hero image (roofing-1.webp)');
    img2.src = '/images/services/roofing/roofing-1.webp';
  }, []);

  const openContactModal = () => {
    setIsContactModalOpen(true);
  };

  const closeContactModal = () => {
    setIsContactModalOpen(false);
  };

  // Carousel navigation functions
  const nextStep = () => {
    setCurrentStep((prev) => (prev + 1) % installationProcess.length);
  };

  const prevStep = () => {
    setCurrentStep((prev) => (prev - 1 + installationProcess.length) % installationProcess.length);
  };

  const goToStep = (stepIndex) => {
    setCurrentStep(stepIndex);
  };



  const installationProcess = [
    {
      icon: FaClipboardCheck,
      title: "Initial Assessment",
      description: "We perform a comprehensive evaluation of your roof condition and provide a personalized solution with transparent pricing."
    },
    {
      icon: FaTools,
      title: "Team Assembly",
      description: "Our experienced crew leaders organize the project timeline and coordinate all aspects of your roofing work."
    },
    {
      icon: FaHardHat,
      title: "Quality Construction",
      description: "Skilled craftsmen deliver superior installation work under the supervision of our quality assurance team."
    },
    {
      icon: FaCheckCircle,
      title: "Final Review",
      description: "Comprehensive quality check, warranty documentation, and continued support for your peace of mind."
    }
  ];


  return (
    <main className="roofing-page" id="main-content">
      <Helmet>
        <title>RC Roofing - Roofing Services</title>
        <meta name="description" content="Professional roofing services including installation, repair, and maintenance. Licensed & insured roofing contractor serving Detroit metro with 500+ completed projects." />
        <meta name="keywords" content="roofing, roof installation, roof repair, roofing contractor, Detroit roofing, Michigan roofing, roof maintenance, commercial roofing" />
        <link rel="canonical" href="https://rcroofing.com/roofing" />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://rcroofing.com/roofing" />
        <meta property="og:title" content="Roofing Services | RC Roofing | Professional Roof Installation & Repair" />
        <meta property="og:description" content="Professional roofing services including installation, repair, and maintenance. Licensed & insured roofing contractor with 500+ completed projects." />
        <meta property="og:image" content="https://rcroofing.com/images/services/roofing/roofing1.webp" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Roofing Services | RC Roofing | Professional Roof Installation & Repair" />
        <meta name="twitter:description" content="Professional roofing services including installation, repair, and maintenance." />
        <meta name="twitter:image" content="https://rcroofing.com/images/services/roofing/roofing1.webp" />
      </Helmet>
      
      <div className="page-header">
        <div className="container">
          <div className="page-nav">
            <Link to="/all-services" className="back-btn">
              <FaArrowLeft className="back-icon" />
              Back to All Services
            </Link>
            {/* Desktop Navigation */}
            <div className="page-nav-right desktop-nav">
              <button 
                className="nav-item" 
                onClick={handleContactClick} 
                title="Call us"
              >
                <FaPhone className="nav-icon" />
                Call us
              </button>
              
              <ReusableDropdown
                triggerText="Services"
                items={navigationServices}
                className="roofing-services-dropdown"
                triggerClassName="roofing-nav-item"
                iconComponent={FaHardHat}
                showIcons={true}
              />
              
              <Link 
                to="/roof-insurance" 
                className="nav-item" 
                title="Roof Insurance"
              >
                <FaShieldAlt className="nav-icon" />
                Insurance
              </Link>
              
              <Link 
                to="/gallery" 
                className="nav-item" 
                title="Our Gallery"
              >
                <FaImages className="nav-icon" />
                Our Gallery
              </Link>
            </div>

            {/* Mobile Navigation */}
            <div className="mobile-nav">
              <button 
                className="hamburger-btn"
                onClick={toggleMobileMenu}
                aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
                aria-expanded={isMobileMenuOpen}
              >
                {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
              </button>
            </div>
          </div>
          <h1 className="page-title">Roofing Services</h1>
          <p className="page-subtitle">
            Professional roof installation, repair, and maintenance services for residential and commercial properties
          </p>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="page-mobile-menu">
          <div className="page-mobile-menu-header">
            <div className="page-mobile-menu-title">Navigation</div>
            <button 
              className="page-mobile-close-btn"
              onClick={closeMobileMenu}
              aria-label="Close menu"
            >
              <FaTimes />
            </button>
          </div>
          
          <div className="page-mobile-menu-content">
            <button 
              className="page-mobile-nav-item"
              onClick={(e) => {
                handleContactClick(e);
                closeMobileMenu();
              }}
            >
              <FaPhone className="nav-icon" />
              Call us
            </button>
            
            <ReusableDropdown
              triggerText="Services"
              items={navigationServices}
              className="page-mobile-dropdown mobile-nav"
              iconComponent={FaHardHat}
              showIcons={true}
              onItemClick={closeMobileMenu}
            />
            
            <Link 
              to="/roof-insurance"
              className="page-mobile-nav-item"
              onClick={closeMobileMenu}
            >
              <FaShieldAlt className="nav-icon" />
              Insurance
            </Link>
            
            <Link 
              to="/gallery"
              className="page-mobile-nav-item"
              onClick={closeMobileMenu}
            >
              <FaImages className="nav-icon" />
              Our Gallery
            </Link>
          </div>
        </div>
      )}
      

      {/* Hero Section */}
      <section className="roofing-hero" style={{
        backgroundImage: `url('/images/services/roofing/roofing-8.webp')`
      }}>
        <div className="roofing-hero-content">
          <div className="roofing-hero-text">
            <h1 ref={titleRef} className="roofing-hero-title animate">
              Professional Roofing Services & Installation
            </h1>
            <p className="roofing-hero-subtitle">
              Your Trusted Partner for Roof Installation and Maintenance
            </p>
            <p className="roofing-hero-description">
              Whether you need a complete roof replacement, emergency repairs, or routine maintenance, 
              having the right roofing contractor can make all the difference. At RC Roofing, we go beyond 
              standard roofing services we provide comprehensive solutions that protect your investment.
            </p>
            <div className="roofing-hero-buttons">
              <button 
                className="roofing-hero-button primary" 
                onClick={openContactModal}
                aria-label="Request roofing services - open contact form"
              >
                Request Roofing Services
              </button>
              <button 
                className="roofing-hero-button secondary" 
                onClick={() => navigate('/all-services')}
                aria-label="View all available services"
              >
                View All Services
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Before/After Drone Video Section */}
      <section className="roofing-video-showcase" ref={videoRef}>
        <div className="roofing-container">
          <div className="roofing-section-header">
            <h2 className="video-section-title">See Our Work in Action</h2>
            <p className="video-section-subtitle">
              Experience the transformation with our exclusive drone footage. Compare the before and after of our professional roofing installation.
            </p>
          </div>
          <div className="roofing-video-container">
            <BeforeAfterVideoSlider
              beforeVideo="/videos/BEFORE.mp4"
              afterVideo="/videos/AFTER.mp4"
              alt="Drone footage comparing roofing before and after installation"
              autoplay={false}
              loop={true}
              muted={true}
            />
          </div>
          <div className="video-feature-note">
            <p><strong>Interactive Video:</strong> Drag the slider to compare before and after footage. Click play to watch the transformation unfold.</p>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="roofing-why-choose-us" ref={whyTrustUsRef}>
        <div className="roofing-container">
          <div className="why-choose-content">
            <div className="why-choose-left">
              <h2 className="why-choose-title">
                WHY CHOOSE <br /><span className="brand-name">RC ROOFING</span>?
              </h2>
              <p className="why-choose-description">
                Our Roofing Experts have 12 years of experience in roof replacement and will work with you to build a customized, affordable solution to increase the value and longevity of your home.
              </p>
            </div>
            <div className="why-choose-right">
              <div className="why-choose-cards">
                <div className="why-choose-card">
                  <div className="card-icon">
                    <FaAward />
                  </div>
                  <h3 className="card-title">Quality Workmanship</h3>
                  <p className="card-description">
                    Our teams take great care with every project, from setup to cleanup. Every shingle in place, no nails left behind.
                  </p>
                </div>
                <div className="why-choose-card">
                  <div className="card-icon">
                    <FaTools />
                  </div>
                  <h3 className="card-title">Expert Installation</h3>
                  <p className="card-description">
                    Your personalized project team will be with you every step of the way, completing your installation with precision and care.
                  </p>
                </div>
                <div className="why-choose-card">
                  <div className="card-icon">
                    <FaCheckCircle />
                  </div>
                  <h3 className="card-title">Trusted Partners</h3>
                  <p className="card-description">
                    We source our materials from trusted manufacturers who drive innovation and offer the best roofing products on the market.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services We Specialize In Section */}
      <section className="roofing-services-hero" style={{
        backgroundImage: `url('/images/services/roofing/roofing-4.webp')`
      }}>
        <div className="roofing-services-hero-content">
          <div className="roofing-services-hero-text">
            <h2 className="roofing-services-hero-title">Services We Specialize In</h2>
            <p className="roofing-services-hero-subtitle">Comprehensive roofing solutions for every property type and material preference</p>
            <p className="roofing-services-hero-description">
              From residential homes to commercial facilities, we provide expert roofing services tailored to your specific needs. 
              Our team specializes in various roofing materials and installation techniques to ensure the perfect solution for your property.
            </p>
          </div>
          <div className="roofing-services-grid">
            <div className="roofing-service-card">
              <h3 className="roofing-service-title">Residential</h3>
              <p className="roofing-service-description">Expert roofing services for homes, townhouses, and residential properties with personalized attention to detail.</p>
            </div>
            <div className="roofing-service-card">
              <h3 className="roofing-service-title">Commercial</h3>
              <p className="roofing-service-description">Professional commercial roofing solutions for businesses, warehouses, and industrial facilities with minimal disruption.</p>
            </div>
            <div className="roofing-service-card">
              <h3 className="roofing-service-title">Flat Roofs</h3>
              <p className="roofing-service-description">Specialized flat roofing solutions including EPDM, TPO, and modified bitumen systems for commercial applications.</p>
            </div>
            <div className="roofing-service-card">
              <h3 className="roofing-service-title">Metal Roofs</h3>
              <p className="roofing-service-description">Durable metal roofing systems offering superior longevity, energy efficiency, and modern aesthetic appeal.</p>
            </div>
            <div className="roofing-service-card">
              <h3 className="roofing-service-title">New Construction</h3>
              <p className="roofing-service-description">Have architectural plans ready? We'll bring your vision to life with expert roof construction, ensuring quality craftsmanship from the ground up.</p>
            </div>
            <button 
              className="roofing-service-card roofing-quote-card" 
              onClick={openContactModal}
              aria-label="Get a free quote for roofing services"
            >
              <h3 className="roofing-quote-text">Get a Free Quote</h3>
            </button>
          </div>
        </div>
      </section>

      {/* Installation Process Section */}
      <section className="roofing-installation" ref={installationRef}>
        <div className="roofing-container">
          {/* Installation Process Header */}
          <div className="roofing-section-header roofing-section-header-enhanced">
            <h2>From Start to Finish</h2>
            <p>From consultation to completion, we ensure quality at every step of your roofing project</p>
          </div>
          
          {/* Installation Process 3D Carousel */}
          <div className="roofing-installation-carousel">
            <div className="roofing-carousel-container">
              <div 
                className="roofing-process-slider" 
                style={{ 
                  transform: `translateX(-${currentStep * 100}%)`,
                  transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                {installationProcess.map((step, index) => (
                  <div key={index} className="roofing-process-card">
                    <div className="roofing-step-icon">
                      <step.icon />
                    </div>
                    <div className="roofing-step-content">
                      <h3 className="roofing-step-title">{step.title}</h3>
                      <p className="roofing-step-description">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Carousel Navigation */}
            <div className="roofing-carousel-navigation" role="group" aria-label="Installation process navigation">
              <button 
                className="roofing-carousel-btn prev-btn" 
                onClick={prevStep}
                aria-label="Previous step in installation process"
              >
                <FaArrowLeft />
              </button>
              
              <div className="roofing-carousel-dots" role="tablist" aria-label="Installation steps">
                {installationProcess.map((_, index) => (
                  <button
                    key={index}
                    className={`roofing-carousel-dot ${index === currentStep ? 'active' : ''}`}
                    onClick={() => goToStep(index)}
                    role="tab"
                    aria-selected={index === currentStep}
                    aria-label={`Go to step ${index + 1}: ${installationProcess[index].title}`}
                    tabIndex={index === currentStep ? 0 : -1}
                  />
                ))}
              </div>
              
              <button 
                className="roofing-carousel-btn next-btn" 
                onClick={nextStep}
                aria-label="Next step in installation process"
              >
                <FaArrowLeft style={{ transform: 'rotate(180deg)' }} />
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Contact Modal */}
      <ContactModal 
        isOpen={isContactModalOpen}
        onClose={closeContactModal}
        formTitle="Let’s Start Your Roofing Project"
        formSubtitle="Ready to protect your property with professional roofing services? Fill out the form below and let's discuss your roofing needs."
      />
    </main>
  );
};

export default RoofingPage;